/**
 * Where a visitor came from, read off the URL they are standing on.
 *
 * The lead row has always had columns for this — `source_source`, `source_media`,
 * `source_campaign`, `source_term`, `source_refer_url` — and the config-driven form has
 * always had a `context` prop documented as "attribution carried on the first submit".
 * Nothing ever filled it in, so every config-driven lead was attributed "Direct" no matter
 * which ad it arrived from. This is that missing half.
 *
 * ─── Why it is read here and not on the server ──────────────────────────────────────
 * The UTM parameters live in the visitor's address bar. The server sees them only if the
 * client sends them, and the drop that needs them may happen minutes later in a background
 * sweep — long after the page is gone. So they are read once, at capture, and persisted on
 * the lead row; see server/migrations/20260824140000-add-source-term-to-leads.js.
 *
 * ─── Empty string, never undefined ──────────────────────────────────────────────────
 * Every value defaults to "", matching how `source_source` is written server-side
 * (`data?.source_source || ""`). An organic visit genuinely has no campaign, and one
 * absent-value spelling across the whole family is what keeps "no term" from meaning three
 * different things depending on which code path wrote the row.
 */

export interface LeadAttribution {
  source_source: string;
  source_media: string;
  source_campaign: string;
  /** utm_term — the keyword or ad group a paid click arrived on. */
  source_term: string;
  source_refer_url: string;
  source_path: string;
  source_params: string;
}

const EMPTY: LeadAttribution = {
  source_source: "",
  source_media: "",
  source_campaign: "",
  source_term: "",
  source_refer_url: "",
  source_path: "",
  source_params: "",
};

/**
 * Read the current page's attribution.
 *
 * Returns all-empty during SSR and on any failure — a malformed URL must not stop a form
 * rendering, and an unattributed lead is worth infinitely more than no lead.
 */
export function readLeadAttribution(): LeadAttribution {
  if (typeof window === "undefined") return { ...EMPTY };
  try {
    const params = new URLSearchParams(window.location.search);
    const get = (key: string) => (params.get(key) ?? "").trim();
    return {
      source_source: get("utm_source"),
      source_media: get("utm_medium"),
      source_campaign: get("utm_campaign"),
      source_term: get("utm_term"),
      // The page that referred them here, which is a different fact from the page they are
      // on — the CRM wants both.
      source_refer_url: document.referrer || "",
      source_path: window.location.origin + window.location.pathname,
      source_params: window.location.search.replace(/^\?/, ""),
    };
  } catch {
    return { ...EMPTY };
  }
}
